import * as React from 'react'
import './style/domain-data-form.scss'
import { classnames } from '@halo/_utils/classnames'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { DomainService } from '@halo/services/domain.service'
import { DomainDto } from '@halo/dtos/domain.dto'
import { UserService } from '@halo/services/user.service'
import { AppState } from '@halo/stores/app.state'
import { DomainDetailsWell } from '@halo/components/domain-data-form/domain-details-well'
import { DomainServerWell } from '@halo/components/domain-data-form/domain-server-well'
import { DomainPromptWell } from '@halo/components/domain-data-form/domain-prompt-well'
import { SendIntegrationsWell } from '@halo/components/domain-data-form/domain-send-integrations-well'
import { DomainEcommWell } from '@halo/components/domain-data-form/domain-ecomm-well'
import { DomainPageAnalysisConfigWell } from '@halo/components/domain-data-form/domain-page-analysis-config-well'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { IPageAnalysisConfig } from '@pushly/aqe/lib/interfaces'
import { hydrateDomainModel } from '@halo/_utils/models-hydration'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { AppService } from '@halo/services'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { Domain } from '@pushly/models/lib/structs/domain'
import { DomainNotificationSettings } from '@pushly/aqe/lib/components'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { extractFriendlyErrorMessage, extractMinMaxFriendlyError } from '@pushly/aqe/lib/utils/exception'
import { DomainNotificationSettingsProps } from '@pushly/aqe/lib/components/domain-notification-settings/domain-notification-settings'

interface IDomainDataForm {
    className?: string
    domainId?: number
}

interface IDDFState {
    loading: boolean
    domain?: DomainDto
    pageAnalysisConfigs?: IPageAnalysisConfig[]
    account?: AccountDto
}

export class DomainDataForm extends React.Component<IDomainDataForm, IDDFState> {
    public readonly state: IDDFState = {
        loading: true,
    }

    protected appState: AppState
    protected appSvc: AppService
    protected domainService: DomainService
    protected accountService: AccountService
    protected userService: UserService
    protected pageAnalysisConfigsService: PageAnalysisConfigsService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appSvc = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)
        this.pageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)
    }

    public componentDidMount() {
        this.reloadState()
    }

    public async reloadState() {
        this.setState({
            ...this.state,
            loading: true,
        })

        const res = await Promise.all([this.fetchRequestedDomain(this.props.domainId), this.fetchPageAnalysisConfigs()])

        const [domain, pageAnalysisConfigs] = res

        this.setState({
            ...this.state,
            domain,
            pageAnalysisConfigs,
            loading: false,
        })
    }

    public render() {
        return (
            <div className={classnames('ddf', this.props.className)}>
                <div className={classnames('ddf-wrapper')}>
                    <DomainDetailsWell
                        loading={this.isLoading}
                        domain={this.state.domain}
                        onDomainUpdated={this.handleDomainUpdated}
                    />

                    <DomainServerWell
                        loading={this.isLoading}
                        domain={this.state.domain}
                        onDomainUpdated={this.handleDomainUpdated}
                    />

                    <SendIntegrationsWell
                        loading={this.isLoading}
                        domain={this.state.domain}
                        onDomainUpdated={this.handleDomainUpdated}
                    />

                    {this.state.domain && (
                        <DomainNotificationSettings
                            title="General Notification Settings"
                            loading={this.isLoading}
                            hideFooter={true}
                            showGeneralSettingsWell={true}
                            showNotificationAutomationWell={true}
                            showInternalEditorAccessFields={this.appState.abilityStore.can(
                                AbilityAction.UPDATE,
                                asCaslSubject(SubjectEntity.DOMAIN, {
                                    ...this.state.domain,
                                    statusId: this.state.domain?.status_id,
                                }),
                            )}
                            domain={hydrateDomainModel(this.state.domain)}
                            fetchCustomMacroFields={() => this.domainService.getCustomMacroFields(this.state.domain.id)}
                            getAppContainer={this.appSvc.getAppContainer}
                            onEditorSubmit={this.onGeneralDomainEditSubmit}
                            wellStyle="halo"
                        />
                    )}

                    <DomainPromptWell
                        loading={this.isLoading}
                        domain={this.state.domain}
                        onDomainUpdated={this.handleDomainUpdated}
                    />

                    <DomainEcommWell
                        loading={this.isLoading}
                        domain={this.state.domain}
                        onDomainUpdated={this.handleDomainUpdated}
                    />

                    {this.appState.currentUser?.isInternalDevUser && (
                        <DomainPageAnalysisConfigWell
                            loading={this.isLoading}
                            domain={this.state.domain}
                            pageAnalysisConfigs={this.state.pageAnalysisConfigs}
                            onDomainUpdated={this.handleDomainUpdated}
                        />
                    )}
                </div>
            </div>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loading
    }

    public onGeneralDomainEditSubmit = async (
        builder: Parameters<DomainNotificationSettingsProps['onEditorSubmit']>[0],
    ) => {
        let resOk = builder.isEmpty

        if (!builder.isEmpty) {
            try {
                const values = Domain.parseJsonObject(builder.buildPartial(), { excludeDefaultValues: true })

                const { ok, data } = await this.domainService.patchByDomainId(this.props.domainId, values)
                if (ok) {
                    await this.handleDomainUpdated(data)
                    resOk = true
                }
            } catch (error) {
                if (error instanceof InvalidFieldException || error instanceof MissingFieldException) {
                    if (
                        error.field === 'deliveryThrottleMaxSeconds' &&
                        (error.constraint === 'min' || error.constraint === 'max')
                    ) {
                        error = extractMinMaxFriendlyError(error, [0, 15], 'minutes')
                    } else {
                        error = extractFriendlyErrorMessage(error)
                    }
                }
                simpleNotification('error', error.message)
            }
        }

        return resOk
    }

    protected handleDomainUpdated = async (updates: Partial<DomainDto> | Partial<Domain>) => {
        this.setState(({ domain }) => ({
            domain: {
                ...domain,
                ...updates,
            } as DomainDto,
        }))
    }

    protected async fetchPageAnalysisConfigs() {
        let configs: IPageAnalysisConfig[] = []

        if (this.appState.currentUser?.isInternalDevUser) {
            const res = await this.pageAnalysisConfigsService.fetchAll({
                query: { pagination: 0 },
            })

            if (res?.data) {
                configs = res.data
            }
        }

        return configs
    }

    protected async fetchRequestedDomain(domainId: number) {
        const { data: domain } = await this.domainService.fetchById(domainId, {
            showLoadingScreen: false,
            cancellationKey: 'ddf-fetch',
        })

        return domain
    }
}
